import type { DRDrawingViewer } from "../DRDrawingViewer";
import { trackFeature } from "@/analytics/drawing-analytics";
import type { InteractiveLayer } from "./InteractiveLayer";
import type { OverlayManager } from "./OverlayManager";
import type { RulerOverlay, RulerConfig, HitTestResult } from "./types";
import { OverlayType, CoordinateSystem, DEFAULT_RULER_CONFIG } from "./types";
import { getOverlayTheme } from "./OverlayTheme";
import { graduationSpacing } from "./rulerScale";
import { paintGraduations, paintRulerHandle } from "./rulerPaint";
import { hasTouchScreen, rulerGrabPolicy } from "./rulerGrab";
import { RULER_SIZE_CONFIGS, type RulerSize } from "./rulerSizes";

/**
 * RulerController - Manages horizontal and vertical rulers.
 *
 * Rulers display DWG coordinate graduations along canvas edges.
 * Features:
 * - Automatic spacing calculation based on zoom level
 * - Draggable origin handles (visible buttons)
 * - Redraws on view change
 *
 * Renders a ruler overlay on the canvas.
 */
export class RulerController {
  private viewer: DRDrawingViewer;
  private interactiveLayer: InteractiveLayer;
  private overlayManager: OverlayManager;
  private config: RulerConfig;

  // Ruler IDs
  private horizontalRulerId: string | null = null;
  private verticalRulerId: string | null = null;

  // Overlay IDs (cleared and recreated on each update)
  private overlayIds: string[] = [];

  // State
  private _origin: { x: number; y: number } = { x: 0, y: 0 };
  private _visible = false;
  private _navigating = false;
  private _draggingHandle: "horizontal" | "vertical" | null = null;
  // Grabbed by its handle or its bar (see rulerGrab).
  private _grabZone: "handle" | "body" = "handle";
  // Distance between the pointer and the bar edge when grabbed: the bar keeps
  // that point under the pointer instead of jumping to it.
  private _grabOffset = 0;
  private _moved = false;
  private readonly _touchScreen = hasTouchScreen();
  private _grab = rulerGrabPolicy(false, this._touchScreen);

  // WCS anchor positions for ruler bars.
  // NaN | null = default edge position (same behaviour as before first drag or origin set).
  // FIXME : Have to add null value because localstorage does not seem to be able to store NaN, it turns it into null.
  // Once set, the bar tracks this WCS coordinate during zoom/pan, clamped to viewport.
  private _horizontalAnchorY: number | null = NaN;
  private _verticalAnchorX: number | null = NaN;

  // Called when anchors change (drag end or origin set) — used for persistence.
  onAnchorChanged: ((hY: number, vX: number) => void) | null = null;

  // Height covered by floating UI along the bottom of the canvas (contextual
  // hint), in canvas pixels: the vertical ruler handle sits just above it.
  private _vHandleBottomInset = 0;
  // Gap between the vertical ruler handle and the bottom of the canvas (or
  // the floating UI there).
  private static readonly V_HANDLE_BOTTOM_GAP = 8;

  // Notified with the distance (canvas pixels) between the vertical ruler and
  // the canvas right edge whenever it changes: the tab bubbles move aside to
  // keep a ruler stuck to that edge visible.
  private _verticalRulerListener: ((offsetFromRight: number) => void) | null =
    null;
  private _lastVerticalRulerOffset: number | null = null;

  // Width of the floating toolbar over the left edge of the canvas, in canvas
  // pixels: the horizontal ruler handle sits just right of it.
  private _hHandleOffsetX = 0;

  // View change subscription
  private unsubscribeViewChange: (() => void) | null = null;

  // Throttle state for performance optimization
  private _lastUpdateTime = 0;
  private static readonly THROTTLE_INTERVAL = 50; // ~20fps max during navigation
  private _pendingUpdate = false;
  private _rafId: number | null = null;

  constructor(
    viewer: DRDrawingViewer,
    interactiveLayer: InteractiveLayer,
    overlayManager: OverlayManager,
    config: Partial<RulerConfig> = {},
  ) {
    this.viewer = viewer;
    this.interactiveLayer = interactiveLayer;
    this.overlayManager = overlayManager;
    this.config = { ...DEFAULT_RULER_CONFIG, ...config };
  }

  // ============== Public API ==============

  /**
   * Show the rulers.
   */
  show(): void {
    if (this._visible) return;
    this._visible = true;

    // Subscribe to view change events
    this.unsubscribeViewChange = this.interactiveLayer.onViewChanged(() => {
      this.update();
    });

    this.createRulers();
    this.drawAll();
    // Fresh overlays carry no offsets: hit zones must match the handles as
    // drawn (below the tab bubbles, right of the toolbar), and the tab
    // bubbles must learn where the vertical ruler now is.
    this.syncHitTestOffsets();
    this.overlayManager.redraw();
  }

  /**
   * Hide the rulers.
   */
  hide(): void {
    if (!this._visible) return;
    this._visible = false;

    // Unsubscribe from view change events
    this.unsubscribeViewChange?.();
    this.unsubscribeViewChange = null;

    this.clearAll();
    this.overlayManager.redraw();
  }

  /**
   * Get ruler visibility.
   */
  get visible(): boolean {
    return this._visible;
  }

  /**
   * Get ruler origin (0 point) in world coordinates.
   */
  get origin(): { x: number; y: number } {
    return { ...this._origin };
  }

  /**
   * Set ruler origin (0 point) in world coordinates.
   */
  set origin(point: { x: number; y: number }) {
    this._origin = { ...point };
    if (this._visible) {
      this.update();
      this.overlayManager.redraw();
    }
  }

  /**
   * Suppress ruler rendering while the view moves (pinch, pan, wheel zoom).
   * Rulers are hidden immediately and restored with a full redraw when done.
   */
  setNavigating(navigating: boolean): void {
    if (!this._visible) return;
    this._navigating = navigating;
    if (navigating) {
      if (this._rafId !== null) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      this._pendingUpdate = false;
      this.clearOverlays();
      this.overlayManager.redraw();
    } else {
      this.doUpdate();
      this.overlayManager.redraw();
    }
  }

  /**
   * Update rulers after view change.
   * Throttled to ~20fps during continuous navigation for performance.
   */
  update(): void {
    if (!this._visible || this._navigating) return;

    const now = performance.now();
    const elapsed = now - this._lastUpdateTime;

    // Throttle: skip if too soon, but schedule a final update
    if (elapsed < RulerController.THROTTLE_INTERVAL) {
      if (!this._pendingUpdate) {
        this._pendingUpdate = true;
        this._rafId = requestAnimationFrame(() => {
          this._pendingUpdate = false;
          this._rafId = null;
          this.doUpdate();
        });
      }
      return;
    }

    this._lastUpdateTime = now;
    this.doUpdate();
  }

  /**
   * Perform the actual ruler update (internal).
   */
  private doUpdate(): void {
    if (!this._visible) return;
    this.clearOverlays();
    this.drawAll();
    this.syncHitTestOffsets();
  }

  // ============== WCS Bar Position Helpers ==============

  private getHRulerY(): number {
    const canvas = this.viewer.canvas;
    if (!this._horizontalAnchorY || isNaN(this._horizontalAnchorY)) {
      return canvas.height - this.config.thickness;
    }
    const screen = this.viewer.worldToScreen({
      x: 0,
      y: this._horizontalAnchorY,
    });
    return Math.max(
      0,
      Math.min(canvas.height - this.config.thickness, screen.y),
    );
  }

  private getVRulerX(): number {
    const canvas = this.viewer.canvas;
    if (!this._verticalAnchorX || isNaN(this._verticalAnchorX)) {
      return (
        canvas.width -
        this.config.thickness -
        RulerController.RIGHT_TOOLBAR_OFFSET
      );
    }
    const screen = this.viewer.worldToScreen({
      x: this._verticalAnchorX,
      y: 0,
    });
    return Math.max(
      0,
      Math.min(canvas.width - this.config.thickness, screen.x),
    );
  }

  private syncHitTestOffsets(): void {
    const canvas = this.viewer.canvas;
    this.updateRulerOffset(
      "horizontal",
      canvas.height - this.config.thickness - this.getHRulerY(),
    );
    const verticalOffset =
      canvas.width - this.config.thickness - this.getVRulerX();
    this.updateRulerOffset("vertical", verticalOffset);
    if (this.verticalRulerId) {
      this.interactiveLayer.updateInteractive(this.verticalRulerId, {
        handleOffsetY: this.getVHandleOffsetY(),
      } as Partial<RulerOverlay>);
    }
    if (verticalOffset !== this._lastVerticalRulerOffset) {
      this._lastVerticalRulerOffset = verticalOffset;
      this._verticalRulerListener?.(verticalOffset);
    }
    if (this.horizontalRulerId) {
      this.interactiveLayer.updateInteractive(this.horizontalRulerId, {
        handleOffsetX: this._hHandleOffsetX,
      } as Partial<RulerOverlay>);
    }
  }

  /**
   * Declare the floating toolbar over the left edge of the canvas (its width
   * in canvas pixels, 0 when there is none): the horizontal ruler handle moves
   * right of it — otherwise a ruler dragged or set mid-plan would hide its
   * handle under the toolbar.
   */
  setHorizontalHandleOffset(offsetX: number): void {
    if (offsetX === this._hHandleOffsetX) return;
    this._hHandleOffsetX = offsetX;
    if (!this._visible) return;
    this.update();
    this.overlayManager.redraw();
  }

  /**
   * Declare the floating UI along the bottom of the canvas (its height in
   * canvas pixels, 0 when there is none): the vertical ruler handle sits a few
   * pixels above it, at the bottom of the ruler.
   */
  setVerticalHandleBottomInset(inset: number): void {
    if (inset === this._vHandleBottomInset) return;
    this._vHandleBottomInset = inset;
    if (!this._visible) return;
    this.update();
    this.overlayManager.redraw();
  }

  /**
   * Distance from the canvas top to the vertical ruler handle's own top
   * margin (cf. vHandleY): the handle sits at the bottom of the ruler, a few
   * pixels above the bottom of the canvas — clear of the floating tab
   * bubbles at the top.
   */
  private getVHandleOffsetY(): number {
    const canvas = this.viewer.canvas;
    return Math.max(
      0,
      canvas.height -
        this._vHandleBottomInset -
        RulerController.V_HANDLE_BOTTOM_GAP -
        this.config.handleSize -
        5,
    );
  }

  /**
   * Follow the distance between the vertical ruler and the canvas right edge
   * (canvas pixels). The listener is called at once, then on every change.
   */
  setVerticalRulerListener(
    listener: ((offsetFromRight: number) => void) | null,
  ): void {
    this._verticalRulerListener = listener;
    const canvas = this.viewer.canvas;
    this._lastVerticalRulerOffset =
      canvas.width - this.config.thickness - this.getVRulerX();
    listener?.(this._lastVerticalRulerOffset);
  }

  /**
   * Update ruler configuration dynamically.
   */
  updateConfig(partialConfig: Partial<RulerConfig>): void {
    this.config = { ...this.config, ...partialConfig };

    // Update the handleSize on existing ruler overlays for hit testing
    if (partialConfig.handleSize !== undefined) {
      this.updateRulerOverlays({ handleSize: partialConfig.handleSize });
    }

    if (this._visible) {
      this.update();
      this.overlayManager.redraw();
    }
  }

  /**
   * Apply theme colors based on the drawing background color.
   */
  applyTheme(backgroundColor: number): void {
    const theme = getOverlayTheme(backgroundColor);
    this.updateConfig({
      backgroundColor: theme.rulerBackground,
      lineColor: theme.rulerLineColor,
      textColor: theme.rulerTextColor,
      originColor: theme.rulerOriginColor,
      handleBorderColor: theme.rulerHandleBorder,
    });
  }

  /**
   * Set the ruler handle scale.
   * @param scale Scale multiplier
   */
  setHandleScale(scale: number): void {
    this.updateConfig({
      handleSize: DEFAULT_RULER_CONFIG.handleSize * scale,
    });
  }

  /**
   * Set both WCS anchors and redraw. Call on drawing open to restore persisted position.
   */
  setAnchors(hY: number, vX: number): void {
    this._horizontalAnchorY = hY;
    this._verticalAnchorX = vX;
    if (this._visible) {
      this.update();
      this.overlayManager.redraw();
    }
  }

  // ============== Drag Handling ==============

  /**
   * Stylus mode (the finger no longer draws): the finger moves a ruler by its
   * bar and the handles are hidden (see rulerGrabPolicy).
   */
  setStylusMode(stylusMode: boolean): void {
    this._grab = rulerGrabPolicy(stylusMode, this._touchScreen);
    this.updateRulerOverlays({
      showHandle: this._grab.showHandles,
      barGrabbedBy: this._grab.barGrabbedBy,
    });
    this.redrawNow();
  }

  /**
   * Bar thickness (Ruler tool panel): graduations and labels follow it.
   */
  setSize(size: RulerSize): void {
    const sizeConfig = RULER_SIZE_CONFIGS[size];
    if (sizeConfig.thickness === this.config.thickness) return;
    this.config = { ...this.config, ...sizeConfig };
    this.updateRulerOverlays({
      thickness: sizeConfig.thickness,
      hitRadius: sizeConfig.thickness,
    });
    this.redrawNow();
  }

  /** Same changes on both ruler overlays (hit testing reads them). */
  private updateRulerOverlays(updates: Partial<RulerOverlay>): void {
    for (const id of [this.horizontalRulerId, this.verticalRulerId]) {
      if (id) this.interactiveLayer.updateInteractive(id, updates);
    }
  }

  /** Not throttled: a setting change is a one-off, the rulers follow now. */
  private redrawNow(): void {
    if (!this._visible || this._navigating) return;
    this.doUpdate();
    this.overlayManager.redraw();
  }

  /**
   * Handle drag start on a ruler, by its handle or its bar.
   */
  onDragStart(hit: HitTestResult): void {
    if (hit.zone !== "handle" && hit.zone !== "body") return;
    const ruler = hit.overlay as RulerOverlay;
    this._draggingHandle = ruler.orientation;
    this._grabZone = hit.zone;
    this._moved = false;
    this._grabOffset =
      ruler.orientation === "horizontal"
        ? hit.screenPoint.y - this.getHRulerY()
        : hit.screenPoint.x - this.getVRulerX();
  }

  /**
   * Handle drag move on a ruler.
   */
  onDragMove(screenPoint: { x: number; y: number }): void {
    if (!this._draggingHandle) return;

    const canvas = this.viewer.canvas;
    this._moved = true;

    if (this._draggingHandle === "horizontal") {
      const defaultY = canvas.height - this.config.thickness;
      const barY = screenPoint.y - this._grabOffset;
      if (barY >= defaultY) {
        this._horizontalAnchorY = NaN;
      } else {
        const worldPos = this.viewer.screenToWorld({ x: 0, y: barY });
        this._horizontalAnchorY = worldPos.y;
      }
    } else {
      const defaultX =
        canvas.width -
        this.config.thickness -
        RulerController.RIGHT_TOOLBAR_OFFSET;
      const barX = screenPoint.x - this._grabOffset;
      if (barX >= defaultX) {
        this._verticalAnchorX = NaN;
      } else {
        const worldPos = this.viewer.screenToWorld({ x: barX, y: 0 });
        this._verticalAnchorX = worldPos.x;
      }
    }

    this.syncHitTestOffsets();
    this.update();
    this.overlayManager.redraw();
  }

  /**
   * Update a ruler overlay's edgeOffset for accurate hit testing.
   */
  private updateRulerOffset(
    orientation: "horizontal" | "vertical",
    offset: number,
  ): void {
    const rulerId =
      orientation === "horizontal"
        ? this.horizontalRulerId
        : this.verticalRulerId;
    if (rulerId) {
      this.interactiveLayer.updateInteractive(rulerId, {
        edgeOffset: offset,
      } as Partial<RulerOverlay>);
    }
  }

  /**
   * Handle drag end on a ruler. A tap without moving changes nothing.
   */
  onDragEnd(): void {
    if (this._draggingHandle && this._moved) {
      trackFeature("ruler_moved", {
        via: this._grabZone === "body" ? "ruler" : "handle",
      });
      this.onAnchorChanged?.(
        this._horizontalAnchorY || NaN,
        this._verticalAnchorX || NaN,
      );
    }
    this._draggingHandle = null;
  }

  // ============== Ruler Creation ==============

  private createRulers(): void {
    // Create horizontal ruler (bottom)
    this.horizontalRulerId = this.interactiveLayer.addInteractive({
      type: OverlayType.Ruler,
      interactive: true,
      coordinateSystem: CoordinateSystem.World,
      orientation: "horizontal",
      snapOptions: {
        acceptObject: false,
        acceptOrtho: false,
        acceptPolar: false,
      },
      origin: this._origin,
      edgeOffset: 0,
      thickness: this.config.thickness,
      handleSize: this.config.handleSize,
      showHandle: this._grab.showHandles,
      barGrabbedBy: this._grab.barGrabbedBy,
      hitRadius: this.config.thickness,
      cursor: "ew-resize",
      style: {
        strokeColor: this.config.lineColor,
        fillColor: this.config.backgroundColor,
      },
      // Drag handlers - called directly by InteractiveLayer
      onDragStart: (hit) => this.onDragStart(hit),
      onDragMove: (screen) => this.onDragMove(screen),
      onDragEnd: () => this.onDragEnd(),
    } as Omit<RulerOverlay, "id">);

    // Create vertical ruler (right)
    this.verticalRulerId = this.interactiveLayer.addInteractive({
      type: OverlayType.Ruler,
      interactive: true,
      coordinateSystem: CoordinateSystem.World,
      orientation: "vertical",
      snapOptions: {
        acceptObject: false,
        acceptOrtho: false,
        acceptPolar: false,
      },
      origin: this._origin,
      edgeOffset: 0,
      thickness: this.config.thickness,
      handleSize: this.config.handleSize,
      showHandle: this._grab.showHandles,
      barGrabbedBy: this._grab.barGrabbedBy,
      hitRadius: this.config.thickness,
      cursor: "ns-resize",
      style: {
        strokeColor: this.config.lineColor,
        fillColor: this.config.backgroundColor,
      },
      // Drag handlers - called directly by InteractiveLayer
      onDragStart: (hit) => this.onDragStart(hit),
      onDragMove: (screen) => this.onDragMove(screen),
      onDragEnd: () => this.onDragEnd(),
    } as Omit<RulerOverlay, "id">);
  }

  private clearAll(): void {
    if (this.horizontalRulerId) {
      this.interactiveLayer.removeInteractive(this.horizontalRulerId);
      this.horizontalRulerId = null;
    }
    if (this.verticalRulerId) {
      this.interactiveLayer.removeInteractive(this.verticalRulerId);
      this.verticalRulerId = null;
    }
    this.clearOverlays();
  }

  private clearOverlays(): void {
    for (const id of this.overlayIds) {
      this.overlayManager.remove(id);
    }
    this.overlayIds = [];
  }

  // ============== Drawing ==============

  private drawAll(): void {
    const canvas = this.viewer.canvas;

    // Ensure canvas has size
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn("[RulerController] Canvas has no size, skipping draw");
      return;
    }

    // Draw backgrounds
    this.drawBackground("horizontal");
    this.drawBackground("vertical");

    const spacing = graduationSpacing(
      this.viewer.screenDistanceToWorld(this.config.minimumSpacing),
      this.config.spacings,
    );
    if (spacing > 0) {
      this.drawGraduations("horizontal", spacing);
      this.drawGraduations("vertical", spacing);
    }

    // Handles, unless the rulers are grabbed by their bar (see rulerGrab)
    if (this._grab.showHandles) this.drawOriginHandles();
  }

  private drawBackground(orientation: "horizontal" | "vertical"): void {
    const canvas = this.viewer.canvas;

    if (orientation === "horizontal") {
      // Horizontal ruler background
      const rulerY = this.getHRulerY();
      const id = this.overlayManager.createRectangle(
        0,
        rulerY,
        canvas.width,
        this.config.thickness,
        {
          fillColor: this.config.backgroundColor,
          strokeColor: this.config.lineColor,
          strokeWidth: 1,
        },
      );
      this.overlayIds.push(id);
    } else {
      // Vertical ruler background
      const rulerX = this.getVRulerX();
      const id = this.overlayManager.createRectangle(
        rulerX,
        0,
        this.config.thickness,
        canvas.height,
        {
          fillColor: this.config.backgroundColor,
          strokeColor: this.config.lineColor,
          strokeWidth: 1,
        },
      );
      this.overlayIds.push(id);
    }
  }

  private drawGraduations(
    orientation: "horizontal" | "vertical",
    spacing: number,
  ): void {
    this.overlayIds.push(
      ...paintGraduations(
        this.overlayManager,
        this.config,
        this.viewer,
        this.viewer.canvas,
        {
          orientation,
          position:
            orientation === "horizontal"
              ? this.getHRulerY()
              : this.getVRulerX(),
          origin: this._origin,
          spacing,
        },
      ),
    );
  }

  // Toolbar offsets - set to 0 since canvas is now positioned within the layout
  // (canvas starts below header/draggable bar; the floating left toolbar is
  // handled by _hHandleOffsetX)
  private static readonly HEADER_OFFSET = 0;
  private static readonly RIGHT_TOOLBAR_OFFSET = 0;

  /**
   * Draw position handles for moving rulers.
   * - Horizontal ruler: handle at bottom-left corner of visible canvas
   * - Vertical ruler: handle at top-right corner of visible canvas
   */
  private drawOriginHandles(): void {
    // Horizontal ruler handle (bottom-left of visible canvas, right of the
    // floating left toolbar)
    this.overlayIds.push(
      ...paintRulerHandle(
        this.overlayManager,
        this.config,
        {
          x: this._hHandleOffsetX + this.config.handleSize / 2 + 5,
          y: this.getHRulerY() + this.config.thickness / 2,
        },
        "horizontal",
      ),
    );

    // Vertical ruler handle (bottom of the ruler, above the contextual hint)
    this.overlayIds.push(
      ...paintRulerHandle(
        this.overlayManager,
        this.config,
        {
          x: this.getVRulerX() + this.config.thickness / 2,
          y:
            RulerController.HEADER_OFFSET +
            this.getVHandleOffsetY() +
            this.config.handleSize / 2 +
            5,
        },
        "vertical",
      ),
    );
  }

  // ============== Lifecycle ==============

  // Handler reference for cleanup
  private _originSetHandler: ((event: Event) => void) | null = null;
  private _attachedCanvas: HTMLCanvasElement | null = null;

  /**
   * Attach origin handler to canvas.
   * Listens for "originset" custom events from OriginDragger.
   */
  attachOriginHandler(canvas: HTMLCanvasElement): void {
    if (this._attachedCanvas) {
      this.detachOriginHandler();
    }

    this._originSetHandler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        origin: { x: number; y: number };
      }>;
      const origin = customEvent.detail.origin;
      // Reset ruler bar positions to follow the new origin
      this._horizontalAnchorY = origin.y;
      this._verticalAnchorX = origin.x;
      this.origin = origin;
      this.onAnchorChanged?.(this._horizontalAnchorY, this._verticalAnchorX);
    };

    canvas.addEventListener("originset", this._originSetHandler);
    this._attachedCanvas = canvas;
  }

  /**
   * Detach origin handler from canvas.
   */
  detachOriginHandler(): void {
    if (this._attachedCanvas && this._originSetHandler) {
      this._attachedCanvas.removeEventListener(
        "originset",
        this._originSetHandler,
      );
    }
    this._originSetHandler = null;
    this._attachedCanvas = null;
  }

  /**
   * Dispose the ruler controller.
   */
  dispose(): void {
    // Cancel any pending throttled update
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._pendingUpdate = false;

    this.detachOriginHandler();
    this.unsubscribeViewChange?.();
    this.unsubscribeViewChange = null;
    this.clearAll();
  }
}
